import { format } from "date-fns"
import {
	AppEventHistory,
	AppEventHistoryType,
	AppEventHistorySubType,
	AppWabaInfo
} from "@/@integrations/WABA/protocols/wabaAccountInfo"

export type WabaAppInfoResponse = {
	phoneQualityLabel: string
	accountStatusLabel: string
	messagingLimitLabel: string
}

export type WabaEventHistoryFormatted = {
	history: {
		eventTypeLabel: string
		eventSubTypeLabel?: string
		createdAt: string
		payload: Record<string, unknown>
	}
	warnings?: {
		hasRestriction?: boolean
		restrictionsInfo?: ActiveRestrictionInfo[]
	}
}

export type ActiveRestrictionInfo = {
	type: string
	expiration: string
}

const EVENT_TIMESTAMP_FORMAT = "dd/MM/yyyy HH:mm:ss"
const RESTRICTION_EXPIRATION_FORMAT = "dd/MM/yyyy HH:mm"

const eventTypeKeyToLabel: Record<AppEventHistoryType, string> = {
	account_update: "ATUALIZAÇÃO DE CONTA",
	message_template_components_update: "ATUALIZAÇÃO DE COMPONENTES DO TEMPLATE",
	phone_number_name_update: "ATUALIZAÇÃO DE NOME DO NÚMERO",
	business_capability_update: "ATUALIZAÇÃO DE CAPACIDADE DO NEGÓCIO",
	account_review_update: "ATUALIZAÇÃO DE REVISÃO DE CONTA",
	phone_number_quality_update: "ATUALIZAÇÃO DE QUALIDADE DO NÚMERO",
	flows: "-"
}

const eventSubTypeKeyToLabel: Record<AppEventHistorySubType, string> = {
	ACCOUNT_RESTRICTION: "CONTA RESTRITA",
	ACCOUNT_VIOLATION: "VIOLAÇÃO DE CONTA",
	BUSINESS_VERIFICATION_STATUS_UPDATE: "ATUALIZAÇÃO DE VERIFICAÇÃO DO NEGÓCIO",
	MM_LITE_TERMS_SIGNED: "TERMOS MM LITE ASSINADOS",
	PARTNER_APP_INSTALLED: "APP DE PARCEIRO INSTALADO",
	PARTNER_ADDED: "PARCEIRO ADICIONADO",
	UPGRADE: "UPGRADE",
	FLAGGED: "-",
	UNFLAGGED: "-",
	FLOW_STATUS_CHANGE: "-"
}

const PHONE_QUALITY_LABELS: Record<AppWabaInfo["phoneQuality"], string> = {
	high: "Alta qualidade",
	medium: "Qualidade média",
	low: "Baixa qualidade",
	unknown: ""
}

const ACCOUNT_STATUS_LABELS: Record<AppWabaInfo["status"], string> = {
	active: "Ativa",
	banned: "Banida",
	unknown: ""
}

const CONVERSATION_LIMIT_LABELS: Record<string, string> = {
	50: "50 mensagens",
	250: "250 mensagens",
	"1k": "1.000 mensagens",
	"10k": "10.000 mensagens",
	"100k": "100.000 mensagens",
	not_set: "Não definido",
	unlimited: "Ilimitado",
	unknown: ""
}

const ACCOUNT_RESTRICTION_LABELS: Record<string, string> = {
	RESTRICTED_ADD_PHONE_NUMBER_ACTION: "Adição de número restrita",
	RESTRICTED_BIZ_INITIATED_MESSAGING: "Inicialização de conversas restrita",
	RESTRICTED_CUSTOMER_INITIATED_MESSAGING: "Respostas a clientes restrita"
}

const isAccountRestrictionEvent = (event: AppEventHistory): boolean =>
	event.type === "account_update" && event.subType === "ACCOUNT_RESTRICTION"

const isAccountViolationEvent = (event: AppEventHistory): boolean =>
	event.type === "account_update" && event.subType === "ACCOUNT_VIOLATION"

export const sanitizeWabaAppInfo = (appInfo: AppWabaInfo): WabaAppInfoResponse => ({
	accountStatusLabel: ACCOUNT_STATUS_LABELS[appInfo.status],
	phoneQualityLabel: PHONE_QUALITY_LABELS[appInfo.phoneQuality],
	messagingLimitLabel: CONVERSATION_LIMIT_LABELS[appInfo.conversationLimit]
})

const resolveEventSubTypeLabel = (event: AppEventHistory): string | undefined => {
	if (!event.subType) return "-"

	const baseLabel = eventSubTypeKeyToLabel[event.subType] ?? event.subType

	if (isAccountViolationEvent(event)) {
		const payload = event.payload as { violation_info?: { violation_type?: string } }
		const violationReason = payload.violation_info?.violation_type

		if (violationReason) {
			return `${baseLabel} (${violationReason})`
		}
	}

	return baseLabel
}

const resolveAccountRestrictionWarnings = (event: AppEventHistory): WabaEventHistoryFormatted["warnings"] | undefined => {
	if (!isAccountRestrictionEvent(event)) return undefined

	const payload = event.payload as { restriction_info?: Array<{ restriction_type: string; expiration: string }> }
	const restrictions = payload.restriction_info ?? []

	const activeRestrictionsInfo = restrictions.map((restriction) => {
		const expirationDate = new Date(restriction.expiration)
		const isExpirationDateStillActive = expirationDate > new Date()

		if (!isExpirationDateStillActive) return null

		return {
			type: ACCOUNT_RESTRICTION_LABELS[restriction.restriction_type] || restriction.restriction_type,
			expiration: format(expirationDate, RESTRICTION_EXPIRATION_FORMAT)
		}
	}).filter(Boolean) as Array<{ type: string; expiration: string }>

	if (activeRestrictionsInfo.length === 0) return undefined

	return {
		hasRestriction: true,
		restrictionsInfo: activeRestrictionsInfo
	}
}

export const sanitizeWabaEvent = (event: AppEventHistory): WabaEventHistoryFormatted => {
	const formattedEventTime = format(new Date(Number(event.createdAt)), EVENT_TIMESTAMP_FORMAT) ?? "-"

	const formattedEvent: WabaEventHistoryFormatted = {
		history: {
			eventTypeLabel: eventTypeKeyToLabel[event.type] ?? event.type,
			createdAt: formattedEventTime,
			eventSubTypeLabel: resolveEventSubTypeLabel(event),
			payload: event.payload
		}
	}

	const warnings = resolveAccountRestrictionWarnings(event)

	if (warnings) {
		formattedEvent.warnings = warnings
	}

	return formattedEvent
}
